'use client';

import { BookCard, EmptyState, Spinner } from '@lectura/ui';
import { BookOpen } from 'lucide-react';
import Link from 'next/link';
import { useAuth } from '@/lib/auth';
import { useLibrary } from '@/lib/queries';
import { useHydrated } from '@/lib/use-hydrated';

export interface FeaturedBooksProps {
  /** Hány publikációt mutasson — alapból 5. */
  limit?: number;
}

/**
 * A kezdőlap „Frissen feltöltött kiadványok" szakaszát tölti élesen a
 * `catalog-svc` legfrissebb 5 publikációjával. A `catalog-svc` `/publications`
 * végpontja `TenantScope`-os (Bearer-token kötelező), ezért bejelentkezetlen
 * renderben a szekció rejtve marad — így nincs 401-zaj a Network tab-on.
 * Backend-első, nincs mock-fallback — üres / hibás állapotban értelmes
 * üres-state jelenik meg.
 */
export function FeaturedBooks({ limit = 5 }: FeaturedBooksProps) {
  const hydrated = useHydrated();
  const isLogged = useAuth((s) => s.isAuthenticated());
  const { data, isLoading, isError } = useLibrary({});

  if (!hydrated || !isLogged) return null;

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner className="size-7" />
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState
        icon={<BookOpen />}
        title="Nem érjük el a könyvtárat"
        description="A katalógus szolgáltatás átmenetileg nem elérhető. Próbáld újra később."
      />
    );
  }

  const items = (data?.data ?? []).slice(0, limit);

  if (items.length === 0) {
    return (
      <EmptyState
        icon={<BookOpen />}
        title="Még nincs publikált kiadvány"
        description="Töltsd fel az első ePUB-ot az admin felületen, és itt megjelenik."
      />
    );
  }

  return (
    <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
      {items.map((pub) => (
        <Link key={pub.id} href={`/read/${pub.id}` as const} aria-label={pub.title}>
          <BookCard
            title={pub.title}
            authors={pub.contributors.filter((c) => c.role === 'author').map((c) => c.name)}
            coverUrl={pub.coverUrl ?? undefined}
          />
        </Link>
      ))}
    </div>
  );
}
